import React, { useEffect, useState } from "react";
import {
  Card, Form, Input, Select, DatePicker, Button, Upload, Alert, Table, Row, Col, Tooltip, Tag,
} from "antd";
import { UploadOutlined, PlusOutlined, EyeOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import { Modal } from "antd";
import BlogDescriptionModal, { outlineHasContent } from "./BlogDescriptionModal";
import { buildCheckboxColumnFilter, distinctOptions } from "../../components/TableFilterDropdown";

const PRIORITY_CLASS = { Low: "priority-low", Medium: "priority-medium", High: "priority-high", Urgent: "priority-urgent" };
const STATUS_CLASS = {
  Pending: "gfx-status--neutral", Assigned: "gfx-status--info", "In Progress": "gfx-status--info",
  "Waiting for Review": "gfx-status--warn", "Revision Requested": "gfx-status--warn", Revised: "gfx-status--info",
  Approved: "gfx-status--success", Completed: "gfx-status--success", Overdue: "gfx-status--danger", Cancelled: "gfx-status--neutral",
};
// Same friendly mapping as the request detail page's hero pill — without it
// this table showed raw enum text ("Waiting for Review") while the detail
// page for the same request showed "Under Review".
const STAGE_LABEL = {
  Pending: "New", Assigned: "New", "In Progress": "In Progress",
  "Waiting for Review": "Under Review", "Revision Requested": "Change Requested", Revised: "Revised",
  Completed: "Completed", Overdue: "Overdue", Cancelled: "Cancelled",
};

// A delivery date is a calendar date, not a point in time — normalizing to
// UTC-midnight of the picked Y-M-D keeps it meaning the same day regardless
// of the viewer's browser timezone (see GraphicsRequests.js in brand-manager
// for the full explanation of the bug this avoids).
const toDeliveryDateISO = (momentDate) => (momentDate ? `${momentDate.format("YYYY-MM-DD")}T00:00:00.000Z` : null);

function CreateBlogWriteRequest() {
  const { request } = useRequest();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [members, setMembers] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [createVisible, setCreateVisible] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null); // request _id being edited, or null when creating
  const [editingOriginalAssignee, setEditingOriginalAssignee] = useState(null);
  const assignedToWatch = Form.useWatch("assignedTo", form);
  const [outline, setOutline] = useState(undefined); // structured blog description, built via BlogDescriptionModal
  const [descModalOpen, setDescModalOpen] = useState(false);
  // Column filters below (Client/Assigned By/Assigned To/Priority/Status)
  // are client-side against whatever's already loaded, same pattern as
  // Staff Management's Presence column — see TableFilterDropdown.js.
  const [rowFilter, setRowFilter] = useState({});
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  // Set when this create form was opened from a specific client's Calendar
  // page — that client is then fixed (picker hidden, shown as plain text)
  // instead of asked for again.
  const [lockedClient, setLockedClient] = useState(null); // { _id, name } | null

  const fetchMembers = () => {
    request({
      url: apiPath.blogWriteTeamMembers,
      method: "GET",
      onSuccess: (data) => setMembers(data?.data?.members || []),
      onError: () => {},
    });
  };

  const fetchRequests = () => {
    setLoading(true);
    request({
      url: apiPath.blogWriteRequests,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setRequests(data?.data?.requests || []);
      },
      onError: () => setLoading(false),
    });
  };

  const fetchClients = () => {
    setLoadingClients(true);
    request({
      url: apiPath.getClients + "?search=&page=1&limit=200",
      method: "GET",
      onSuccess: (data) => {
        setLoadingClients(false);
        setClients(data?.data?.clients || []);
      },
      onError: () => setLoadingClients(false),
    });
  };

  useEffect(() => {
    fetchMembers();
    fetchRequests();
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Arriving here via the calendar's "Send Request to Blog Writer" button
  // (FullCalender.js) opens straight into the create form instead of
  // landing on the plain list — replaces navigate("/linkedin-connect")-style
  // deep-links elsewhere. selectedDate (the day the staff member clicked on
  // the calendar) prefills Delivery Date so it isn't re-entered. When that
  // calendar was a specific client's own calendar, clientId/clientName ride
  // along too — the client picker below is then skipped entirely.
  useEffect(() => {
    if (!location.state?.autoOpenCreate) return;
    setEditingId(null);
    setCreateVisible(true);
    if (location.state.selectedDate) {
      form.setFieldsValue({ deliveryDate: moment(location.state.selectedDate) });
    }
    if (location.state.clientId) {
      setLockedClient({ _id: location.state.clientId, name: location.state.clientName || "" });
      form.setFieldsValue({ clientId: location.state.clientId, clientNameRef: location.state.clientName || "" });
    }
    // Clear the nav state so refreshing this page (or navigating back to
    // it later) doesn't re-open the modal every time.
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishEditDetails = (values) => {
    const payload = { ...values };
    delete payload.assignedTo;
    delete payload.reassignReason;
    if (payload.deliveryDate) payload.deliveryDate = toDeliveryDateISO(payload.deliveryDate);
    // The details endpoint now also accepts new reference files, so it has
    // to be multipart the same way create's POST is — a plain JSON body
    // would hang on the server's formidable parser.
    const formData = new FormData();
    Object.entries(payload).forEach(([key, val]) => {
      if (val !== undefined && val !== null) formData.append(key, val);
    });
    // outline is a nested object (headers/subheaders included) — JSON-encode
    // it onto the multipart body the same way this codebase already does for
    // other structured fields sent alongside file uploads (e.g. pollData in
    // AccessModuleController).
    formData.append("outline", JSON.stringify(outline || {}));
    fileList.forEach((f) => formData.append("referenceFiles", f));
    request({
      url: apiPath.blogWriteRequestUpdate(editingId),
      method: "PUT",
      data: formData,
      onSuccess: (data) => {
        setSaving(false);
        if (data.status === 200) {
          ShowToast("Blog write request updated successfully", Severty.SUCCESS);
          setCreateVisible(false);
          setEditingId(null);
          setEditingOriginalAssignee(null);
          form.resetFields();
          setFileList([]);
          setOutline(undefined);
          fetchRequests();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        setSaving(false);
        ShowToast(err?.response?.data?.message || "Failed to update request", Severty.ERROR);
      },
    });
  };

  // Admin always overrides the workload block (per spec) so reassignment
  // here never needs a blocked-modal detour — only the short reason gate
  // from BlogWriteService.assignRequest applies.
  const submitEdit = (values) => {
    setSaving(true);
    const reassigning = values.assignedTo !== editingOriginalAssignee;
    if (!reassigning) return finishEditDetails(values);

    request({
      url: apiPath.blogWriteOverrideAssign(editingId),
      method: "PUT",
      data: { assignedTo: values.assignedTo, reason: values.reassignReason, overrideReason: values.reassignReason },
      onSuccess: (data) => {
        if (data.status === 200) {
          finishEditDetails(values);
        } else {
          setSaving(false);
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        setSaving(false);
        ShowToast(err?.response?.data?.message || "Failed to reassign request", Severty.ERROR);
      },
    });
  };

  const onFinish = (values) => {
    if (editingId) return submitEdit(values);
    setSaving(true);
    const formData = new FormData();
    Object.entries(values).forEach(([key, val]) => {
      if (key === "deliveryDate" && val) return formData.append("deliveryDate", toDeliveryDateISO(val));
      if (val !== undefined && val !== null) formData.append(key, val);
    });
    // Admin can always override the workload block per the spec.
    formData.append("override", "true");
    // outline is a nested object — JSON-encode it the same way this codebase
    // already does for other structured fields sent alongside file uploads.
    formData.append("outline", JSON.stringify(outline || {}));
    fileList.forEach((f) => formData.append("referenceFiles", f));

    request({
      url: apiPath.blogWriteRequests,
      method: "POST",
      data: formData,
      onSuccess: (data) => {
        setSaving(false);
        if (data.status === 200) {
          ShowToast("Blog write request created successfully", Severty.SUCCESS);
          setCreateVisible(false);
          form.resetFields();
          setFileList([]);
          setOutline(undefined);
          fetchRequests();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        setSaving(false);
        ShowToast(err?.response?.data?.message || "Failed to create request", Severty.ERROR);
      },
    });
  };

  const openEditModal = (record) => {
    setEditingId(record._id);
    setEditingOriginalAssignee(record.assignedTo?._id || null);
    form.setFieldsValue({
      clientNameRef: record.clientNameRef,
      projectTitle: record.projectTitle,
      deliveryDate: record.deliveryDate ? moment(record.deliveryDate) : null,
      priority: record.priority,
      assignedTo: record.assignedTo?._id || undefined,
      reassignReason: undefined,
    });
    setOutline(record.outline);
    setFileList([]);
    setCreateVisible(true);
  };

  const closeCreateModal = () => {
    setCreateVisible(false);
    setEditingId(null);
    setEditingOriginalAssignee(null);
    setLockedClient(null);
    form.resetFields();
    setFileList([]);
    setOutline(undefined);
  };

  const deleteRequest = (record) => {
    Modal.confirm({
      title: "Delete this blog write request?",
      content: `"${record.projectTitle}" will be removed and taken off the Blog Writer's queue. This can't be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise((resolve, reject) => {
          request({
            url: apiPath.blogWriteRequest(record._id),
            method: "DELETE",
            onSuccess: (data) => {
              if (data.status === 200) {
                ShowToast("Blog write request deleted", Severty.SUCCESS);
                fetchRequests();
                resolve();
              } else {
                ShowToast(data.message, Severty.ERROR);
                reject();
              }
            },
            onError: (err) => {
              ShowToast(err?.response?.data?.message || "Failed to delete request", Severty.ERROR);
              reject();
            },
          });
        }),
    });
  };

  const columns = [
    { title: "Project Title", dataIndex: "projectTitle", key: "projectTitle" },
    {
      title: "Client (internal)", dataIndex: "clientNameRef", key: "clientNameRef",
      ...buildCheckboxColumnFilter({
        rows: requests, rowFilter, setRowFilter, key: "clientNameRef",
        getOptions: (rows) => distinctOptions(rows, (r) => r.clientNameRef),
        getValue: (r) => r.clientNameRef,
      }),
    },
    {
      title: "Assigned By", key: "createdBy", render: (_, r) => r.createdByName || r.createdBy?.name || "-",
      ...buildCheckboxColumnFilter({
        rows: requests, rowFilter, setRowFilter, key: "createdBy",
        getOptions: (rows) => distinctOptions(rows, (r) => r.createdByName || r.createdBy?.name),
        getValue: (r) => r.createdByName || r.createdBy?.name,
      }),
    },
    {
      title: "Assigned To", dataIndex: "assignedTo", key: "assignedTo", render: (a) => a?.name || "Unassigned",
      ...buildCheckboxColumnFilter({
        rows: requests, rowFilter, setRowFilter, key: "assignedTo",
        getOptions: (rows) => distinctOptions(rows, (r) => r.assignedTo?.name || "Unassigned"),
        getValue: (r) => r.assignedTo?.name || "Unassigned",
      }),
    },
    {
      title: "Delivery Date", dataIndex: "deliveryDate", key: "deliveryDate",
      render: (d) => (d ? moment(d).format("MMM D, YYYY") : "-"),
    },
    {
      title: "Priority", dataIndex: "priority", key: "priority",
      render: (p) => (
        <span className="gfx-priority-pill-sm">
          <span className={`gfx-priority-dot ${PRIORITY_CLASS[p] || "priority-medium"}`} />
          {p}
        </span>
      ),
      ...buildCheckboxColumnFilter({
        rows: requests, rowFilter, setRowFilter, key: "priority",
        getOptions: () => ["Low", "Medium", "High", "Urgent"].map((v) => ({ label: v, value: v })),
        getValue: (r) => r.priority,
      }),
    },
    {
      title: "Status", dataIndex: "status", key: "status",
      render: (s, record) => (
        <span>
          <span className={`gfx-status-pill ${STATUS_CLASS[s] || "gfx-status--neutral"}`}>{STAGE_LABEL[s] || s}</span>
          {record.extensionRequest?.status === "pending" && (
            <Tag color="gold" style={{ marginLeft: 6 }}>Extension Pending</Tag>
          )}
        </span>
      ),
      ...buildCheckboxColumnFilter({
        rows: requests, rowFilter, setRowFilter, key: "status",
        getOptions: (rows) => {
          const seen = new Map();
          rows.forEach((r) => {
            if (r.status && !seen.has(r.status)) seen.set(r.status, STAGE_LABEL[r.status] || r.status);
          });
          return [...seen.entries()]
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([value, label]) => ({ label, value }));
        },
        getValue: (r) => r.status,
      }),
    },
    {
      title: "Actions",
      key: "actions",
      width: 170,
      render: (_, record) => (
        <div className="row-actions">
          <Tooltip title="View">
            <button
              className="row-action-btn row-action-view"
              onClick={() => navigate(`/blog-write-requests/${record._id}`)}
            >
              <EyeOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Edit">
            <button
              className="row-action-btn row-action-edit"
              onClick={() => openEditModal(record)}
            >
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button
              className="row-action-btn row-action-delete"
              onClick={() => deleteRequest(record)}
            >
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="gfx-req-hero gfx-oversight-hero">
        <div className="gfx-req-hero-top">
          <div>
            <div className="gfx-req-hero-eyebrow">Blogs</div>
            <h2 className="gfx-req-hero-title">Blogs</h2>
          </div>
          <button className="gfx-hero-cta" onClick={() => { setEditingId(null); setCreateVisible(true); }}>
            <PlusOutlined /> Create Blog Write Request
          </button>
        </div>
      </div>

      <Card bordered={false} className="criclebox tablespace mb-24">
        <Table rowKey="_id" loading={loading} columns={columns} dataSource={requests} />
      </Card>

      <Modal
        open={createVisible}
        title={editingId ? "Edit Blog Write Request" : "Create Blog Write Request"}
        width={800}
        onCancel={closeCreateModal}
        okText={editingId ? "Save Changes" : "Create Request"}
        okButtonProps={{ loading: saving }}
        onOk={() => form.submit()}
        className="gfx-request-modal"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
      >
        {!editingId && (
          <Alert
            type="info"
            showIcon
            message="As an Admin, this request bypasses the Blog Write Team workload block if the member is fully occupied."
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={form} layout="vertical" onFinish={onFinish} className="gfx-request-form">
          {!editingId && (
            <Row gutter={20}>
              <Col span={12}>
                <Form.Item label="Client" required>
                  {lockedClient ? (
                    <Input value={lockedClient.name} disabled />
                  ) : (
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select client"
                      optionFilterProp="label"
                      optionLabelProp="label"
                      loading={loadingClients}
                      value={form.getFieldValue("clientId")}
                      onChange={(value) => {
                        const client = clients.find((c) => c._id === value);
                        form.setFieldsValue({ clientId: value, clientNameRef: client?.name || null });
                      }}
                    >
                      {clients.map((c) => (
                        <Select.Option key={c._id} value={c._id} label={c.name}>
                          {c.name}
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
                {/* Always mounted (not conditionally on !lockedClient) — an
                    unmounted Form.Item's value set via form.setFieldsValue
                    doesn't reliably survive submit in antd v4, which is
                    exactly how a Calendar-locked client silently submitted
                    clientId: null before this fix. */}
                <Form.Item name="clientId" rules={[{ required: true, message: "Select a client" }]} hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="clientNameRef" hidden>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Blog Title" name="projectTitle" rules={[{ required: true, message: "Required" }]}>
                  <Input placeholder="Blog title" />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Form.Item label="Blog Post Outline Framework">
            <div className="blog-desc-trigger">
              <Button onClick={() => setDescModalOpen(true)}>
                {outlineHasContent(outline) ? "Edit Blog Post Outline Framework" : "+ Add Blog Post Outline Framework"}
              </Button>
              {outlineHasContent(outline) && <Tag color="green">Blog Post Outline Framework added ✓</Tag>}
            </div>
          </Form.Item>

          <Row gutter={20}>
            <Col span={8}>
              <Form.Item label="Delivery Date" name="deliveryDate" rules={[{ required: true, message: "Required" }]}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Priority" name="priority" initialValue="Medium">
                <Select options={["Low", "Medium", "High", "Urgent"].map((p) => ({ label: p, value: p }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Assign To" name="assignedTo">
                <Select
                  allowClear
                  placeholder="Blog Write Team member"
                  options={members.map((m) => ({ label: m.name, value: m._id }))}
                />
              </Form.Item>
            </Col>
          </Row>

          {editingId && assignedToWatch !== editingOriginalAssignee && (
            <Form.Item
              label="Reason for Reassignment"
              name="reassignReason"
              rules={[{ required: true, message: "Please add a short reason — it will be shown in the audit log" }]}
            >
              <Input placeholder="e.g. Original assignee is on leave" />
            </Form.Item>
          )}

          <Form.Item
            label={editingId ? "Add Reference Files (optional)" : "Attachments (reference documents, briefs, examples)"}
          >
            <Upload
              multiple
              beforeUpload={(file) => {
                setFileList((prev) => [...prev, file]);
                return false;
              }}
              onRemove={(file) => setFileList((prev) => prev.filter((f) => f.uid !== file.uid))}
              fileList={fileList}
            >
              <Button icon={<UploadOutlined />}>Select Files</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <BlogDescriptionModal
        open={descModalOpen}
        value={outline}
        onChange={setOutline}
        onClose={() => setDescModalOpen(false)}
      />
    </>
  );
}

export default CreateBlogWriteRequest;
